import React, { useState, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  Download,
  ArrowRight,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Target,
  AlertCircle,
  CheckCircle,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { DriverEditor } from './DriverEditor';
import { exportSet } from './exportSet';
import { SentenceClassifier } from './SentenceClassifier';
import type { BucketType } from './classification';
import { useOutcomeDriverStore } from '../../stores/prioritization/outcomeDriverStore';

// ── Types ────────────────────────────────────────────────────────────────────

interface OutcomeDriverSetBuilderProps {
  onBack: () => void;
  cycleId?: string | null;
  cycleName?: string | null;
  returnUrl?: string | null;
}

interface Driver {
  id: string;
  title?: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
  order: number;
  desiredOutcome?: string;
  revisions: Revision[];
}

interface Revision {
  id: string;
  narrative: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  createdAt: string;
}

// ── About section (teaching separated from implementation) ───────────────────

const DRIVER_FIELDS = [
  { label: 'Context of Purpose', body: 'The domain, scope, and time horizon this driver operates within. Answers "what world are we describing?"' },
  { label: 'Current State', body: 'The objective, factual description of how things are today — without editorializing. Establishes baseline.' },
  { label: 'Problem / Opportunity', body: 'What is wrong, missing, or possible given the current state. This is where tension lives.' },
  { label: 'Ramifications', body: 'What happens if this problem goes unaddressed, or what value is left unrealized if this opportunity is missed.' },
  { label: 'Narrative', body: 'The synthesized story that ties the four elements above into a single coherent case for change.' },
];

function AboutOutcomeDrivers({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <div className="border border-primary-200 dark:border-primary-800/60 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Target className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          <span className="text-sm font-semibold text-primary-900 dark:text-primary-300">
            What is an Outcome Driver? How does it work?
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-primary-400" />
          : <ChevronDown className="h-4 w-4 text-primary-400" />
        }
      </button>

      {open && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-t border-primary-100 dark:border-primary-900/40">
          <div className="px-5 py-4 border-b border-[var(--rule)]">
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed max-w-3xl">
              An <strong>Outcome Driver</strong> is a structured artifact that articulates <em>why change is needed</em>
              in a specific area. It answers: what is the current state, what is wrong or possible, what happens if
              we don't act, and what is the coherent case for change? A set of Outcome Drivers establishes the
              motivational foundation for a prioritization cycle — they are the "why" that makes item rankings legible.
            </p>
          </div>
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wider mb-3">
              The five fields of an Outcome Driver
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl">
              {DRIVER_FIELDS.map((f) => (
                <div key={f.label}>
                  <dt className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{f.label}</dt>
                  <dd className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{f.body}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', onDismiss }: { message: string; type?: 'success' | 'error'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3  shadow-lg border text-sm font-medium
      ${type === 'success'
        ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-[var(--green)] dark:border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
        : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-[var(--coral)] dark:border-[var(--coral)] text-[var(--coral)] dark:text-[var(--coral)]'
      }`}
    >
      {type === 'success'
        ? <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
        : <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0" />
      }
      {message}
      <button onClick={onDismiss} className="ml-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">✕</button>
    </div>
  );
}

// ── Driver Card ───────────────────────────────────────────────────────────────

const DriverCard: React.FC<{
  driver: Driver;
  index: number;
  total: number;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}> = ({ driver, index, onEdit, onDelete, onMoveUp, onMoveDown }) => (
  <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] p-4 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
    <div className="flex items-start gap-3">
      {/* Order + reorder controls */}
      <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
        <button
          onClick={onMoveUp}
          disabled={!onMoveUp}
          className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] disabled:opacity-0 transition-colors"
        >
          <ArrowUp className="h-3 w-3" />
        </button>
        <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold text-xs">
          {index + 1}
        </div>
        <button
          onClick={onMoveDown}
          disabled={!onMoveDown}
          className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] disabled:opacity-0 transition-colors"
        >
          <ArrowDown className="h-3 w-3" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {driver.title && (
          <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1 text-sm">
            {driver.title}
          </h3>
        )}
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] line-clamp-3 leading-relaxed">
          {driver.narrative || (
            <span className="italic text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              No narrative yet — click Edit to add content.
            </span>
          )}
        </p>

        {/* Field indicators */}
        <div className="flex flex-wrap gap-2 mt-2.5">
          {[
            { key: 'currentState', label: 'Current State' },
            { key: 'problemOpportunity', label: 'Problem / Opp.' },
            { key: 'ramifications', label: 'Ramifications' },
          ].map(({ key, label }) => {
            const filled = Boolean(driver[key as keyof Driver]);
            return (
              <span
                key={key}
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium
                  ${filled
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                  }`}
              >
                {filled ? '✓' : '○'} {label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
        >
          <Edit className="h-3.5 w-3.5" />
          Edit
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

export const OutcomeDriverSetBuilder: React.FC<OutcomeDriverSetBuilderProps> = ({
  onBack,
  cycleId,
  cycleName,
  returnUrl,
}) => {
  const navigate = useNavigate();
  const { createDriver, createSet } = useOutcomeDriverStore();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showClassifier, setShowClassifier] = useState(false);
  const [classifierText, setClassifierText] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Driver management ────────────────────────────────────────────────────

  const addDriver = () => {
    const newDriver: Driver = {
      id: `drv_${Date.now()}`,
      currentState: '',
      problemOpportunity: '',
      ramifications: '',
      narrative: '',
      order: drivers.length + 1,
      revisions: [],
    };
    setDrivers((prev) => [...prev, newDriver]);
    setEditingDriver(newDriver);
  };

  const updateDriver = (updated: Driver) => {
    const revision: Revision = {
      id: `rev_${Date.now()}`,
      narrative: updated.narrative,
      currentState: updated.currentState,
      problemOpportunity: updated.problemOpportunity,
      ramifications: updated.ramifications,
      createdAt: new Date().toISOString(),
    };
    setDrivers((prev) =>
      prev.map((d) =>
        d.id === updated.id
          ? { ...updated, revisions: [...(updated.revisions || []), revision] }
          : d
      )
    );
    setEditingDriver(null);
  };

  const deleteDriver = (id: string) => {
    setDrivers((prev) => prev.filter((d) => d.id !== id));
  };

  const reorderDrivers = (fromIndex: number, toIndex: number) => {
    setDrivers((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((d, idx) => ({ ...d, order: idx + 1 }));
    });
  };

  // ── Save to workspace (Supabase) ─────────────────────────────────────────

  const handleSave = async (andReturn: boolean = false) => {
    if (drivers.length === 0) {
      showToast('Add at least one outcome driver before saving.', 'error');
      return;
    }
    setIsSaving(true);

    const savedIds: string[] = [];
    for (const d of drivers) {
      const { driver: saved, error } = await createDriver({
        title: d.title || d.narrative?.substring(0, 60) || 'Untitled Driver',
        contextOfPurpose: '',
        currentState: d.currentState,
        problemOpportunity: d.problemOpportunity,
        ramifications: d.ramifications,
        narrative: d.narrative,
        cycleId: cycleId ?? null,
      });
      if (saved) savedIds.push(saved.id);
      if (error) console.warn('[OutcomeDriverSetBuilder] driver save error:', error);
    }

    const setTitle = cycleName
      ? `Outcome Drivers — ${cycleName}`
      : `Outcome Drivers — ${new Date().toLocaleDateString()}`;

    const { set: savedSet, error: setError } = await createSet({
      title: setTitle,
      cycleId: cycleId ?? null,
      driverIds: savedIds,
    });

    setIsSaving(false);

    if (setError || !savedSet) {
      showToast(`Drivers saved but grouping failed: ${setError ?? 'Unknown error'}`, 'error');
      return;
    }

    showToast(`${savedIds.length} driver${savedIds.length !== 1 ? 's' : ''} saved to workspace.`);

    if (andReturn && returnUrl && cycleId) {
      navigate(returnUrl, {
        state: {
          returnedDrivers: savedIds.map((id) => ({ id })),
          fromGenerator: true,
          forCycleId: cycleId,
        },
      });
    }
  };

  // ── Editor view ──────────────────────────────────────────────────────────

  if (editingDriver) {
    return (
      <DriverEditor
        driver={editingDriver}
        contextOfPurpose=""
        onSave={updateDriver}
        onCancel={() => setEditingDriver(null)}
      />
    );
  }

  // ── Main view ────────────────────────────────────────────────────────────

  const sorted = [...drivers].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Target className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
        iconBg="bg-primary-100 dark:bg-primary-900/30"
        title="Outcome Drivers"
        subtitle={
          cycleName
            ? `Building the case for change — for cycle: ${cycleName}`
            : 'Define why change is needed before you prioritize'
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowClassifier((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Classifier
            </button>
            <button
              onClick={() => exportSet({ project: null, drivers, assumptions: [], openQuestions: [], stakeholders: [] })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            {returnUrl ? (
              <>
                <button
                  onClick={() => handleSave(false)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] disabled:opacity-60 transition-colors"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Draft
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  Save & Return to Cycle
                </button>
              </>
            ) : (
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save to Workspace
              </button>
            )}
          </div>
        }
      />

      {/* Teaching — collapsed by default */}
      <AboutOutcomeDrivers open={showAbout} onToggle={() => setShowAbout((v) => !v)} />

      {/* Driver list */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Outcome Drivers
              {drivers.length > 0 && (
                <span className="ml-2 text-xs font-normal text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {drivers.length} driver{drivers.length !== 1 ? 's' : ''}
                </span>
              )}
            </h2>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              Most sets have 1–3 core drivers and rarely more than 5.
            </p>
          </div>
          <button
            onClick={addDriver}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Driver
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            <Target className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1">
              No outcome drivers yet
            </p>
            <p className="text-xs">
              Click "Add Driver" to define the first case for change.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((driver, index) => (
              <DriverCard
                key={driver.id}
                driver={driver}
                index={index}
                total={sorted.length}
                onEdit={() => setEditingDriver(driver)}
                onDelete={() => deleteDriver(driver.id)}
                onMoveUp={index > 0 ? () => reorderDrivers(index, index - 1) : undefined}
                onMoveDown={index < sorted.length - 1 ? () => reorderDrivers(index, index + 1) : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sentence Classifier sidebar */}
      <SentenceClassifier
        isOpen={showClassifier}
        onClose={() => {
          setShowClassifier(false);
          setClassifierText('');
        }}
        initialSentence={classifierText}
        onInsert={(_sentence: string, _bucket: BucketType) => {
          setShowClassifier(false);
        }}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
};
